import * as React from 'react';
import type { CommissionStatementMappingData } from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementMappingRepository';
import { CommissionStatementMappingRepository } from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementMappingRepository';
import { SpreadsheetColumnMap } from '../../Utilities/SpreadsheetColumnMap';
import type { SpreadsheetDataList, SpreadsheetDataRow } from './SpreadsheetMapper';

export interface MappingBuilderProps {
  onChange: (
    value: Array<CommissionStatementMappingData> | null
  ) => Array<CommissionStatementMappingData | null>;
  value: Array<CommissionStatementMappingData> | null;
  data?: SpreadsheetDataList;
  headerRow?: number;
}

export const MappingBuilder = (props: MappingBuilderProps): React.JSX.Element => {
  const [data, setData] = React.useState<Array<CommissionStatementMappingData> | null>(props.value);
  const [dataError, setDataError] = React.useState<string>();
  const [headers, setHeaders] = React.useState<SpreadsheetDataRow | null>(null);

  const mappingRepository = new CommissionStatementMappingRepository();
  const fields = mappingRepository.getFields();
  const columns = SpreadsheetColumnMap.getColumns();

  const handleChangeField = (index: number) => (event: React.ChangeEvent<HTMLSelectElement>) => {
    const fieldName = event.target.name;
    const numberFields = ['column'];
    const fieldValue = numberFields.includes(fieldName)
      ? parseInt(event.target.value)
      : event.target.value;

    const newData = data ? [...data] : [];
    newData[index][fieldName] = fieldValue;

    setData(newData);
    props.onChange(newData);
  };

  const addNewField = () => {
    const newField: CommissionStatementMappingData = {};

    const newData = data ? [...data] : [];
    newData.push(newField);
    setData(newData);
  };

  React.useEffect(() => {
    setDataError(null);

    try {
      mappingRepository.validateFields(data);
    } catch (e) {
      setDataError(e.message);
    }
  }, [data]);

  React.useEffect(() => {
    if (props.data && props.headerRow) {
      const headers = props.data[props.headerRow];
      setHeaders(headers);
    }
  }, [props.headerRow, props.data]);

  React.useEffect(() => {
    setData(props.value);
  }, [props.value]);

  return (
    <div>
      <button onClick={(e) => addNewField()} className="btn btn-secondary btn-sm mb-2">
        <i className="far fa-plus-circle mr-1"></i> Add Field
      </button>
      {(!data || data.length === 0) && <p className="alert alert-info small">No fields mapped</p>}
      {data && data.length > 0 && dataError && (
        <p className="alert alert-info small">{dataError}</p>
      )}
      {data && data.length > 0 && (
        <>
          <div className="row mb-1">
            <div className="col-2">
              <strong>Column</strong>
            </div>
            <div className="col-10">
              <strong>Data Type</strong>
            </div>
          </div>
          {data.map((dataField, dataFieldIndex) => {
            return (
              <div className="row mb-1">
                <div className="col-2">
                  <select
                    className="form-control"
                    onChange={handleChangeField(dataFieldIndex)}
                    name="column"
                  >
                    <option></option>
                    {columns.map((column, columnIndex) => {
                      const columnExists = data.find(
                        (dataSingle) => dataSingle.column === columnIndex
                      );

                      return (
                        <option
                          disabled={columnExists !== undefined}
                          selected={
                            dataField.column !== undefined && dataField.column === columnIndex
                          }
                          value={columnIndex}
                        >
                          {column}
                          {headers && headers[columnIndex] ? ' - ' + headers[columnIndex] : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="col-10">
                  <select
                    className="form-control"
                    onChange={handleChangeField(dataFieldIndex)}
                    name="field"
                  >
                    <option></option>
                    {fields.map((field) => {
                      const fieldExists = data.find(
                        (dataSingle) => dataSingle.field === field.name
                      );

                      return (
                        <option
                          disabled={fieldExists !== undefined}
                          selected={dataField.field === field.name}
                          value={field.name}
                        >
                          {field.label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};
